import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Film, Plus, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreatorDashboardShell } from "@/components/CreatorDashboardShell";
import { ContentCalendarPanel } from "@/components/ContentCalendarPanel";
import { CreatorSettingsPanel } from "@/components/CreatorSettingsPanel";
import { ApprovalPanel } from "@/components/ApprovalPanel";
import { EpisodesPanel } from "@/components/EpisodesPanel";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  creatorEpisodeApprovePath,
  creatorEpisodeProcessPath,
  creatorEpisodePublishPath,
  env,
} from "@/lib/env";
import {
  fetchCreatorConfig,
  type CreatorConfig,
  type EpisodeStatus,
} from "@/lib/creator-config";
import type { SeriesDetail } from "@/lib/series-types";
import { Route as DashboardRoute } from "./dashboard";

export const Route = createFileRoute("/dashboard/")({
  head: () => ({
    meta: [
      { title: "Creator Dashboard — AutoTubber" },
      {
        name: "description",
        content:
          "Track your automated video series: episode pipeline status, approval queue, schedule and connected social accounts.",
      },
    ],
  }),
  component: Dashboard,
});

function statusTone(status: EpisodeStatus) {
  if (status === "PUBLISHED") return "bg-primary/15 text-primary border-primary/40";
  if (status === "FAILED") return "bg-destructive/15 text-destructive border-destructive/40";
  if (status === "PENDING_APPROVAL") return "bg-secondary text-foreground border-border";
  return "bg-muted text-muted-foreground border-border";
}

type SocialAccount = {
  id: string;
  platform: string;
  accountLabel: string;
  channelTitle?: string | null;
  channelThumbUrl?: string | null;
  externalChannelId?: string | null;
  expiresAt: string | null;
};

type AccountLimits = {
  socialAccounts: number;
  used: number;
};

type BillingMe = {
  plan: string;
  limits: {
    series: number;
    episodesPerMonth: number;
    premiumAssets: boolean;
    socialAccounts?: number;
  };
  configured: boolean;
  currency: string | null;
  paidPlans: { key: string; limits: BillingMe["limits"] }[];
};

function Dashboard() {
  const navigate = useNavigate();
  const {
    tab: tabFromSearch,
    reference,
    youtube: youtubeFromSearch,
    reason: youtubeReason,
    created: youtubeCreated,
    updated: youtubeUpdated,
  } = DashboardRoute.useSearch();
  const { ready, user, refresh } = useAuth();
  const [config, setConfig] = useState<CreatorConfig | null>(null);
  const [series, setSeries] = useState<SeriesDetail[]>([]);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [accountLimits, setAccountLimits] = useState<AccountLimits | null>(
    null,
  );
  const [youtubeConfigured, setYoutubeConfigured] = useState(false);
  const [metaConfigured, setMetaConfigured] = useState(false);
  const [tiktokConfigured, setTiktokConfigured] = useState(false);
  const [connectStubMessage, setConnectStubMessage] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [billing, setBilling] = useState<BillingMe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabFromSearch || "overview");
  const [checkoutBusy, setCheckoutBusy] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(
    null,
  );
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  useEffect(() => {
    if (tabFromSearch) setActiveTab(tabFromSearch);
  }, [tabFromSearch]);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate({
        to: env.routes.login,
        search: { redirect: env.routes.dashboard },
        replace: true,
      });
      return;
    }
    if (user.role === "ADMIN") {
      navigate({ to: env.routes.admin, replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!ready || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const [cfg, seriesRes, accountsRes, billingRes] = await Promise.all([
          fetchCreatorConfig(),
          api<{ series: SeriesDetail[] }>(env.paths.creatorSeries),
          api<{
            accounts: SocialAccount[];
            connectStubMessage: string;
            platforms: string[];
            youtubeConfigured?: boolean;
            metaConfigured?: boolean;
            tiktokConfigured?: boolean;
            limits?: AccountLimits;
          }>(env.paths.creatorAccounts),
          api<BillingMe>(env.paths.billingMe),
        ]);
        if (cancelled) return;
        setConfig(cfg);
        setSeries(seriesRes.series);
        setAccounts(accountsRes.accounts);
        setConnectStubMessage(accountsRes.connectStubMessage);
        setPlatforms(accountsRes.platforms);
        setYoutubeConfigured(Boolean(accountsRes.youtubeConfigured));
        setMetaConfigured(Boolean(accountsRes.metaConfigured));
        setTiktokConfigured(Boolean(accountsRes.tiktokConfigured));
        setAccountLimits(accountsRes.limits || null);
        setBilling(billingRes);
        if (!tabFromSearch) {
          const firstTab = cfg.nav.find((n) => n.tab)?.tab;
          if (firstTab) setActiveTab(firstTab);
        }
      } catch (err) {
        if (!cancelled) {
          if (err instanceof ApiError && err.status === 401) {
            navigate({
              to: env.routes.login,
              search: { redirect: env.routes.dashboard },
              replace: true,
            });
            return;
          }
          setError(
            err instanceof ApiError ? err.message : "Could not load dashboard.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, user, navigate, tabFromSearch]);

  useEffect(() => {
    if (!reference || !ready || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ plan: string }>(env.paths.billingVerify, {
          method: "POST",
          body: { reference },
        });
        if (cancelled) return;
        toast.success(`Plan updated to ${res.plan}`);
        await refresh();
        const billingRes = await api<BillingMe>(env.paths.billingMe);
        if (!cancelled) setBilling(billingRes);
        navigate({
          to: env.routes.dashboard,
          search: { tab: "billing" },
          replace: true,
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof ApiError ? err.message : "Payment verification failed",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reference, ready, user, navigate, refresh]);

  // Keep Episodes / Approval tabs fresh while anything is in the pipeline.
  useEffect(() => {
    if (!ready || !user) return;
    const inflight = series.some((s) =>
      s.episodes.some((e) =>
        [
          "QUEUED",
          "GENERATING_SCRIPT",
          "GENERATING_AUDIO",
          "GENERATING_VISUALS",
          "ASSEMBLING",
        ].includes(String(e.status)),
      ),
    );
    if (!inflight) return;
    const id = window.setInterval(() => {
      void api<{ series: SeriesDetail[] }>(env.paths.creatorSeries)
        .then((res) => setSeries(res.series))
        .catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(id);
  }, [
    ready,
    user,
    series
      .flatMap((s) => s.episodes.map((e) => `${e.id}:${e.status}`))
      .join("|"),
  ]);

  const onTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate({
      to: env.routes.dashboard,
      search: { tab },
      replace: true,
    });
  };

  const retryFailedEpisode = async (episodeId: string, topicTitle: string) => {
    setRetryingId(episodeId);
    try {
      await api(creatorEpisodeProcessPath(episodeId), { method: "POST" });
      toast.success(
        `Retrying “${topicTitle}” — recovering finished video if available.`,
      );
      const seriesRes = await api<{ series: SeriesDetail[] }>(
        env.paths.creatorSeries,
      );
      setSeries(seriesRes.series);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not retry episode",
      );
    } finally {
      setRetryingId(null);
    }
  };

  const refreshSeries = async () => {
    const seriesRes = await api<{ series: SeriesDetail[] }>(
      env.paths.creatorSeries,
    );
    setSeries(seriesRes.series);
  };

  const approveEpisode = async (episodeId: string, topicTitle: string) => {
    setApprovingId(episodeId);
    try {
      const res = await api<{ message?: string }>(
        creatorEpisodeApprovePath(episodeId),
        { method: "POST" },
      );
      toast.success(res.message || `Approved “${topicTitle}”`);
      await refreshSeries();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not approve episode",
      );
    } finally {
      setApprovingId(null);
    }
  };

  const publishEpisode = async (episodeId: string, topicTitle: string) => {
    setApprovingId(episodeId);
    try {
      await api(creatorEpisodePublishPath(episodeId), { method: "POST" });
      toast.success(`Publishing “${topicTitle}” to YouTube…`);
      await refreshSeries();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not publish episode",
      );
    } finally {
      setApprovingId(null);
    }
  };

  const startCheckout = async (planKey: string) => {
    setCheckoutBusy(planKey);
    try {
      const res = await api<{ authorizationUrl: string }>(
        env.paths.billingInitialize,
        { method: "POST", body: { plan: planKey } },
      );
      window.location.href = res.authorizationUrl;
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not start checkout",
      );
      setCheckoutBusy(null);
    }
  };

  useEffect(() => {
    if (!youtubeFromSearch) return;
    if (youtubeFromSearch === "connected") {
      toast.success(
        `Channel linked${youtubeCreated ? ` · ${youtubeCreated} new` : ""}${
          youtubeUpdated ? ` · ${youtubeUpdated} updated` : ""
        }`,
      );
      void api<{
        accounts: SocialAccount[];
        limits?: AccountLimits;
      }>(env.paths.creatorAccounts).then((res) => {
        setAccounts(res.accounts);
        setAccountLimits(res.limits || null);
      });
    } else if (youtubeFromSearch === "error") {
      const map: Record<string, string> = {
        limit: "Channel limit reached for your plan — upgrade to link more.",
        no_channel: "No channel/page found on that account.",
        oauth: "Authorization failed. Try again.",
        missing_code: "Authorization was cancelled.",
      };
      toast.error(
        map[youtubeReason || ""] ||
          `Connect failed${youtubeReason ? ` (${youtubeReason})` : ""}`,
      );
    }
    navigate({
      to: env.routes.dashboard,
      search: { tab: "accounts" },
      replace: true,
    });
  }, [
    youtubeFromSearch,
    youtubeReason,
    youtubeCreated,
    youtubeUpdated,
    navigate,
  ]);

  const connectPlatform = async (platform: string) => {
    setConnectingPlatform(platform);
    try {
      const res = await api<{ authorizeUrl?: string }>(
        env.paths.creatorAccountsConnect,
        {
          method: "POST",
          body: { platform },
        },
      );
      if (res.authorizeUrl) {
        window.location.href = res.authorizeUrl;
        return;
      }
      toast.message("Connected");
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? err.message
          : connectStubMessage || "Could not connect",
      );
    } finally {
      setConnectingPlatform(null);
    }
  };

  const disconnectAccount = async (accountId: string) => {
    setDisconnectingId(accountId);
    try {
      await api(`${env.paths.creatorAccounts}/${accountId}`, {
        method: "DELETE",
      });
      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
      setAccountLimits((prev) =>
        prev
          ? { ...prev, used: Math.max(0, prev.used - 1) }
          : prev,
      );
      toast.success("Channel disconnected");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not disconnect",
      );
    } finally {
      setDisconnectingId(null);
    }
  };

  const episodes = useMemo(
    () =>
      series.flatMap((s) =>
        s.episodes.map((e) => ({ ...e, seriesName: s.name, seriesId: s.id })),
      ),
    [series],
  );
  const pendingApproval = episodes.filter((e) => e.status === "PENDING_APPROVAL");
  const failed = episodes.filter((e) => e.status === "FAILED");
  const episodeCount = series.reduce((sum, s) => sum + s._count.episodes, 0);

  if (!ready || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
        Checking session…
      </div>
    );
  }

  return (
    <CreatorDashboardShell
      config={config}
      activeTab={activeTab}
      onTabChange={onTabChange}
      userEmail={user.email}
    >
      <main className="mx-auto w-full max-w-none px-5 py-10 lg:px-8">
        {error && (
          <p className="mb-6 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm">
            {error}
          </p>
        )}

        {activeTab === "overview" && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Your series</h1>
                <p className="mt-1 text-muted-foreground">
                  {loading
                    ? "Loading…"
                    : `${series.length} series · ${episodeCount} episodes generated`}
                </p>
              </div>
              <Button asChild>
                <Link to={env.routes.onboarding}>
                  <Plus className="mr-1 h-4 w-4" /> New series
                </Link>
              </Button>
            </div>

            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {!loading && series.length === 0 && !error && (
                <p className="text-sm text-muted-foreground md:col-span-2">
                  No series yet. Create one from onboarding.
                </p>
              )}
              {series.map((s) => (
                <Link
                  key={s.id}
                  to="/dashboard/series/$seriesId"
                  params={{ seriesId: s.id }}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card transition-colors hover:border-primary/50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">{s.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        {s.category} · {s.narrator.name}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        s.status === "ACTIVE"
                          ? "border-primary/40 bg-primary/15 text-primary"
                          : "border-border bg-muted text-muted-foreground",
                      )}
                    >
                      {s.status}
                    </Badge>
                  </div>
                  <div className="mt-6 flex items-center gap-6 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Film className="h-4 w-4" /> {s._count.episodes} episodes
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CalendarClock className="h-4 w-4" />{" "}
                      {s.postingSchedule?.time
                        ? `${s.postingSchedule.frequency || "scheduled"} · ${s.postingSchedule.time}`
                        : s.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {activeTab === "episodes" && (
          <EpisodesPanel episodes={episodes} statusTone={statusTone} />
        )}

        {activeTab === "approval" && (
          <ApprovalPanel
            pending={pendingApproval}
            failed={failed}
            statusTone={statusTone}
            retryingId={retryingId}
            approvingId={approvingId}
            onRetry={(id, title) => void retryFailedEpisode(id, title)}
            onApprove={(id, title) => void approveEpisode(id, title)}
            onPublish={(id, title) => void publishEpisode(id, title)}
          />
        )}

        {activeTab === "calendar" && <ContentCalendarPanel />}

        {activeTab === "settings" && (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <CreatorSettingsPanel
              config={config}
              series={series}
              onSeriesUpdated={setSeries}
            />
            <div className="mt-10 border-t border-border pt-6">
              <h3 className="font-medium">Per-series extras</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Narrator, background, schedule, and auto-publish stay on each
                series page.
              </p>
              <div className="mt-4 space-y-2">
                {series.map((s) => (
                  <Link
                    key={s.id}
                    to="/dashboard/series/$seriesId"
                    params={{ seriesId: s.id }}
                    className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-sm hover:border-primary/50"
                  >
                    <span className="font-medium">{s.name}</span>
                    <span className="text-muted-foreground">
                      Open series →
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "accounts" && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">Connected channels</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Link channels, then assign each series a destination. Publish
                  currently uploads to YouTube only. {connectStubMessage}
                </p>
              </div>
              {accountLimits && (
                <p className="text-sm text-muted-foreground">
                  {accountLimits.used} / {accountLimits.socialAccounts} channels
                  used
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {platforms.map((platform) => {
                const isYt = platform === "YOUTUBE";
                const isMeta =
                  platform === "INSTAGRAM" || platform === "FACEBOOK";
                const isTt = platform === "TIKTOK";
                const configured = isYt
                  ? youtubeConfigured
                  : isMeta
                    ? metaConfigured
                    : isTt
                      ? tiktokConfigured
                      : false;
                const atLimit =
                  accountLimits != null &&
                  accountLimits.used >= accountLimits.socialAccounts;
                const hint = !configured
                  ? isYt
                    ? "Set GOOGLE_CLIENT_* + YOUTUBE_REDIRECT_URI"
                    : isMeta
                      ? "Set META_APP_ID / SECRET / REDIRECT_URI"
                      : isTt
                        ? "Set TIKTOK_CLIENT_KEY / SECRET / REDIRECT_URI"
                        : "Not configured"
                  : atLimit
                    ? "At plan limit — reconnect refreshes tokens"
                    : "OAuth ready — link an account";
                const label = isYt
                  ? "Connect YouTube"
                  : isMeta
                    ? `Connect ${platform === "INSTAGRAM" ? "Instagram" : "Facebook"}`
                    : isTt
                      ? "Connect TikTok"
                      : "Connect";
                return (
                  <div
                    key={platform}
                    className="rounded-2xl border border-border bg-card p-5 text-center shadow-card"
                  >
                    <Youtube
                      className={cn(
                        "mx-auto h-7 w-7",
                        configured ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <p className="mt-3 font-medium">{platform}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
                    <Button
                      size="sm"
                      className="mt-3 w-full"
                      disabled={
                        connectingPlatform === platform || !configured
                      }
                      onClick={() => void connectPlatform(platform)}
                    >
                      {connectingPlatform === platform ? "Opening…" : label}
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="space-y-3">
              <h3 className="font-medium">Your channels</h3>
              {accounts.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No channels linked yet. Connect a platform above.
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {accounts.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4"
                  >
                    {a.channelThumbUrl ? (
                      <img
                        src={a.channelThumbUrl}
                        alt=""
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                        <Youtube className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {a.channelTitle || a.accountLabel}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.platform}
                        {a.externalChannelId
                          ? ` · ${a.externalChannelId.slice(0, 10)}…`
                          : ""}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={disconnectingId === a.id}
                      onClick={() => void disconnectAccount(a.id)}
                    >
                      {disconnectingId === a.id ? "…" : "Disconnect"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "billing" && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h2 className="text-xl font-semibold">Billing</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Current plan:{" "}
                <span className="font-medium text-foreground">
                  {billing?.plan || user.plan}
                </span>
                {billing?.currency ? ` · ${billing.currency}` : ""}
              </p>
              {billing?.limits && (
                <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
                  <li>Series limit: {billing.limits.series}</li>
                  <li>Episodes / month: {billing.limits.episodesPerMonth}</li>
                  <li>
                    Social channels:{" "}
                    {billing.limits.socialAccounts ?? "—"}
                  </li>
                  <li>
                    Premium assets:{" "}
                    {billing.limits.premiumAssets ? "Included" : "Upgrade required"}
                  </li>
                </ul>
              )}
              {!billing?.configured && (
                <p className="mt-4 text-sm text-amber-500">
                  Paystack is not configured on the server yet. Set PAYSTACK_* env
                  vars to enable checkout.
                </p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {(billing?.paidPlans || []).map((p) => (
                <div
                  key={p.key}
                  className="rounded-2xl border border-border bg-card p-5 shadow-card"
                >
                  <h3 className="text-lg font-semibold">{p.key}</h3>
                  <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                    <li>{p.limits.series} series</li>
                    <li>{p.limits.episodesPerMonth} episodes / month</li>
                    <li>
                      {p.limits.socialAccounts ?? "—"} YouTube channels
                    </li>
                    <li>
                      Premium assets: {p.limits.premiumAssets ? "Yes" : "No"}
                    </li>
                  </ul>
                  <Button
                    className="mt-4 w-full"
                    disabled={
                      !billing?.configured ||
                      checkoutBusy === p.key ||
                      billing.plan === p.key
                    }
                    onClick={() => void startCheckout(p.key)}
                  >
                    {billing?.plan === p.key
                      ? "Current plan"
                      : checkoutBusy === p.key
                        ? "Redirecting…"
                        : `Upgrade to ${p.key}`}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </CreatorDashboardShell>
  );
}
